import is from '@sindresorhus/is';
import { z } from 'zod';

export const payTypeSchema = z.enum(['SALARIED', 'HOURLY']);
export type PayType = z.infer<typeof payTypeSchema>;

export const employmentTypeSchema = z.enum(['TARIF', 'AT']);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

const salaryRangeSchema = z.object({
    maximum: z.number(),
    midpoint: z.number(),
    minimum: z.number(),
});
export type SalaryRange = z.infer<typeof salaryRangeSchema>;

const targetBonusSchema = z.union([z.string(), z.number()]).transform((value) => {
    if (is.number(value) && value >= 0 && value <= 1) {
        return `${Number.parseFloat((value * 100).toFixed(12))}%`;
    }

    return String(value);
});

const payDataSchema = z.object({
    employmentType: employmentTypeSchema,
    fringeBenefits: z.string().default(''),
    longTermIncentive: z.string(),
    payGrade: z.string(),
    payType: payTypeSchema,
    salary: salaryRangeSchema,
    targetBonus: targetBonusSchema,
});
export type PayData = z.infer<typeof payDataSchema>;

const payBandSchema = z.object({
    currency: z.string(),
    name: z.string(),
    payData: payDataSchema,
    region: z.string(),
});
export type PayBand = z.infer<typeof payBandSchema>;

export const translatedContentSchema = z.object({
    description: z.string().default(''),
    language: z.string(),
    name: z.string().default(''),
    progressionCriteria: z.string().nullish(),
});

export type TranslatedContent = z.infer<typeof translatedContentSchema>;

export const careerTrackLevelSchema = z.object({
    description: z.string(),
    level: z.number().int().min(1),
    name: z.string(),
    payBands: payBandSchema.array().optional(),
    payData: payDataSchema.nullable().optional(),
    progressionCriteria: z.string().nullable().optional(),
    translations: translatedContentSchema.array().default([]),
});

export type CareerTrackLevel = z.infer<typeof careerTrackLevelSchema>;

export const careerTrackSchema = z.object({
    id: z.uuid(),
    description: z.string(),
    levels: careerTrackLevelSchema.array(),
    name: z.string(),
    translations: translatedContentSchema.array().default([]),
});

export type CareerTrack = z.infer<typeof careerTrackSchema>;
